// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The panel specification document.
 *
 * This is the file `sigmaKi.py` consumes, so the shape here is dictated by the
 * engine rather than chosen: the key sets mirror its `check_keys()` allow-lists
 * and the emit order mirrors `SPEC_KEY_ORDER` (`sigmaKi.py:129-131`).
 *
 * Unknown keys are deliberately kept rather than dropped. A spec written for a
 * newer engine should survive a trip through the editor unchanged, and telling
 * the user about a key we do not recognise is more useful than deleting it.
 */

export type Vec2 = [number, number];

export type Anchor = 'center' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';

export const ANCHORS: Anchor[] = ['center', 'topleft', 'topright', 'bottomleft', 'bottomright'];

/** Where each anchor sits inside the source-area box, as a fraction. */
export const ANCHOR_FRACTION: Record<Anchor, Vec2> = {
  center: [0.5, 0.5],
  topleft: [0, 0],
  topright: [1, 0],
  bottomleft: [0, 1],
  bottomright: [1, 1],
};

export type SourceSpec =
  | { type: 'auto' }
  | { type: 'annotation'; ref: string }
  | { type: 'rectangle'; tl: Vec2; br: Vec2 };

export interface BoardEntry {
  name?: string;
  file: string;
  source?: SourceSpec;
  tolerance?: number;
  position: Vec2;
  anchor?: Anchor;
  rotation?: number;
  net_rename?: string;
  ref_rename?: string;
}

export type RailType = 'rect' | 'poly' | 'tb' | 'lr' | 'frame';
export type CornerCuts = 'none' | 'both' | 'v' | 'h';

export interface RailEntry {
  type: RailType;
  /** rect */
  at?: Vec2;
  size?: Vec2;
  /** poly */
  points?: Vec2[];
  /** tb | lr */
  thickness?: number;
  space?: number;
  span?: Vec2;
  /** frame */
  width?: number;
  hspace?: number;
  vspace?: number;
  corner_cuts?: CornerCuts;
}

/** Same geometry as a rail, but adds substrate without producing cuts. */
export interface PatchEntry {
  type?: 'rect' | 'poly';
  at?: Vec2;
  size?: Vec2;
  points?: Vec2[];
}

export interface TabEntry {
  origin: Vec2;
  direction: Vec2;
  width?: number;
  sides?: 'both' | 'one';
  max_length?: number;
}

export type CutsType = 'mousebites' | 'vcuts' | 'layer' | 'none';

export interface CutsSpec {
  type?: CutsType;
  drill?: number;
  spacing?: number;
  offset?: number;
  prolong?: number;
  cut_curves?: boolean;
  layer?: string;
  line_width?: number;
}

export interface DefaultsSpec {
  tolerance?: number;
  tab_width?: number;
  tab_max_length?: number;
  net_rename?: string;
  ref_rename?: string;
}

export interface PostSpec {
  mill_radius?: number;
  reconstruct_arcs?: boolean;
  refill_zones?: boolean;
  set_origin?: boolean;
}

export interface PageSpec {
  size?: string | Vec2;
  position?: Vec2;
}

export interface ToolingSpec {
  count?: number;
  hoffset?: number;
  voffset?: number;
  diameter?: number;
}

export interface FiducialsSpec {
  count?: number;
  hoffset?: number;
  voffset?: number;
  copper?: number;
  opening?: number;
}

export interface TextEntry {
  text: string;
  at: Vec2;
  layer?: string;
  height?: number;
  thickness?: number;
  rotation?: number;
}

export interface CopperFillSpec {
  clearance?: number;
}

export interface PanelSpec {
  version?: number;
  units?: string;
  output?: string;
  page?: PageSpec;
  defaults?: DefaultsSpec;
  boards?: BoardEntry[];
  rails?: RailEntry[];
  patches?: PatchEntry[];
  tabs?: TabEntry[];
  manual_cuts?: Vec2[][];
  cuts?: CutsSpec;
  post?: PostSpec;
  tooling?: ToolingSpec;
  fiducials?: FiducialsSpec;
  text?: TextEntry[];
  copper_fill?: CopperFillSpec | boolean;
  [key: string]: unknown;
}

/** Emit order, from `SPEC_KEY_ORDER` in the engine. */
export const SPEC_KEY_ORDER: string[] = [
  'version', 'units', 'output', 'page', 'defaults', 'boards', 'rails', 'patches',
  'tabs', 'manual_cuts', 'cuts', 'post', 'tooling', 'fiducials', 'text', 'copper_fill',
];

/** Accepted top-level keys, from `TOP_LEVEL_KEYS` in the engine. */
export const TOP_LEVEL_KEYS = new Set(SPEC_KEY_ORDER);

const BOARD_KEYS = new Set([
  'name', 'file', 'source', 'tolerance', 'position', 'anchor', 'rotation',
  'net_rename', 'ref_rename',
]);
const SOURCE_KEYS = new Set(['type', 'ref', 'tl', 'br']);
const TAB_KEYS = new Set(['origin', 'direction', 'width', 'sides', 'max_length']);
const CUTS_KEYS = new Set([
  'type', 'drill', 'spacing', 'offset', 'prolong', 'cut_curves', 'layer', 'line_width',
]);
const DEFAULTS_KEYS = new Set([
  'tolerance', 'tab_width', 'tab_max_length', 'net_rename', 'ref_rename',
]);
const POST_KEYS = new Set(['mill_radius', 'reconstruct_arcs', 'refill_zones', 'set_origin']);
const PAGE_KEYS = new Set(['size', 'position']);
const TOOLING_KEYS = new Set(['count', 'hoffset', 'voffset', 'diameter']);
const FIDUCIALS_KEYS = new Set(['count', 'hoffset', 'voffset', 'copper', 'opening']);
const TEXT_KEYS = new Set(['text', 'at', 'layer', 'height', 'thickness', 'rotation']);
const COPPER_FILL_KEYS = new Set(['clearance']);

const RAIL_KEYS: Record<RailType, Set<string>> = {
  rect: new Set(['type', 'at', 'size']),
  poly: new Set(['type', 'points']),
  tb: new Set(['type', 'thickness', 'space', 'span']),
  lr: new Set(['type', 'thickness', 'space', 'span']),
  frame: new Set(['type', 'width', 'hspace', 'vspace', 'corner_cuts']),
};

export function emptySpec(): PanelSpec {
  return {
    version: 1,
    units: 'mm',
    output: 'panel.kicad_pcb',
    defaults: { tolerance: 1, tab_width: 3, tab_max_length: 50 },
    boards: [],
    rails: [],
    tabs: [],
    cuts: { type: 'mousebites', drill: 0.5, spacing: 0.8, offset: 0.25, prolong: 0.5 },
  };
}

// --- validation ---------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function checkKeys(
  section: unknown,
  allowed: ReadonlySet<string>,
  where: string,
  errors: string[],
): void {
  if (!isRecord(section)) return;
  const unknown = Object.keys(section).filter((k) => !allowed.has(k));
  if (unknown.length > 0) {
    errors.push(
      `Unknown key(s) ${JSON.stringify(unknown.sort())} in ${where}. ` +
        `Allowed: ${JSON.stringify([...allowed].sort())}`,
    );
  }
}

/**
 * The same checks the engine makes, run early enough to be useful.
 *
 * Reported rather than thrown: the editor should keep drawing a spec it has
 * complaints about, so the user can see what they are fixing.
 */
export function validateSpec(spec: PanelSpec): string[] {
  const errors: string[] = [];

  checkKeys(spec, TOP_LEVEL_KEYS, 'the top level', errors);

  if (spec.units !== undefined && spec.units !== 'mm') {
    errors.push(`Only units: mm is supported (got ${JSON.stringify(spec.units)})`);
  }

  const boards = spec.boards ?? [];
  if (!Array.isArray(boards) || boards.length === 0) {
    errors.push("Spec contains no 'boards' section");
  } else {
    const names = new Set<string>();
    boards.forEach((b, i) => {
      checkKeys(b, BOARD_KEYS, `boards[${i}]`, errors);
      if (!isRecord(b)) return;
      if (typeof b.file !== 'string' || b.file === '') {
        errors.push(`boards[${i}] needs a 'file'`);
      }
      if (!isVec2(b.position)) {
        errors.push(`boards[${i}] needs 'position: [x, y]'`);
      }
      if (b.anchor !== undefined && !ANCHORS.includes(b.anchor as Anchor)) {
        errors.push(
          `boards[${i}] has unknown anchor ${JSON.stringify(b.anchor)}; ` +
            `use one of ${JSON.stringify(ANCHORS)}`,
        );
      }
      const name = typeof b.name === 'string' ? b.name : `board_${i}`;
      if (names.has(name)) errors.push(`Duplicate board name ${JSON.stringify(name)}`);
      names.add(name);

      if (b.source !== undefined) {
        checkKeys(b.source, SOURCE_KEYS, `boards[${i}].source`, errors);
        const type = isRecord(b.source) ? b.source.type : undefined;
        if (type !== undefined && !['auto', 'annotation', 'rectangle'].includes(type as string)) {
          errors.push(`boards[${i}].source has unknown type ${JSON.stringify(type)}`);
        }
        if (type === 'annotation' && typeof (b.source as { ref?: unknown }).ref !== 'string') {
          errors.push(`boards[${i}].source of type 'annotation' needs a 'ref'`);
        }
      }
    });
  }

  (spec.rails ?? []).forEach((r, i) => {
    if (!isRecord(r)) return;
    const type = r.type as RailType | undefined;
    if (type === undefined || !(type in RAIL_KEYS)) {
      errors.push(
        `rails[${i}] has unknown rail type ${JSON.stringify(type)} ` +
          '(use rect | poly | tb | lr | frame)',
      );
      return;
    }
    checkKeys(r, RAIL_KEYS[type], `rails[${i}]`, errors);
  });

  (spec.patches ?? []).forEach((p, i) => {
    if (!isRecord(p)) return;
    const type = (p.type ?? 'rect') as string;
    if (type !== 'rect' && type !== 'poly') {
      errors.push(`patches[${i}] has unknown type ${JSON.stringify(type)} (use rect | poly)`);
      return;
    }
    checkKeys(p, RAIL_KEYS[type], `patches[${i}]`, errors);
  });

  (spec.tabs ?? []).forEach((t, i) => {
    checkKeys(t, TAB_KEYS, `tabs[${i}]`, errors);
    if (!isRecord(t)) return;
    if (!isVec2(t.origin) || !isVec2(t.direction)) {
      errors.push(`tabs[${i}] needs 'origin: [x, y]' and 'direction: [dx, dy]'`);
    }
    if (t.sides !== undefined && t.sides !== 'both' && t.sides !== 'one') {
      errors.push(`tabs[${i}] sides must be 'both' or 'one'`);
    }
  });

  (spec.text ?? []).forEach((t, i) => {
    checkKeys(t, TEXT_KEYS, `text[${i}]`, errors);
    if (isRecord(t) && typeof t.text !== 'string') errors.push(`text[${i}] needs a 'text'`);
  });

  (spec.manual_cuts ?? []).forEach((c, i) => {
    if (!Array.isArray(c) || c.length < 2) {
      errors.push(`manual_cuts[${i}] needs at least two points`);
    }
  });

  checkKeys(spec.cuts, CUTS_KEYS, 'cuts', errors);
  if (isRecord(spec.cuts) && spec.cuts.type !== undefined) {
    const t = spec.cuts.type as string;
    if (!['mousebites', 'vcuts', 'layer', 'none'].includes(t)) {
      errors.push(`cuts has unknown type ${JSON.stringify(t)}`);
    }
  }
  checkKeys(spec.defaults, DEFAULTS_KEYS, 'defaults', errors);
  checkKeys(spec.post, POST_KEYS, 'post', errors);
  checkKeys(spec.page, PAGE_KEYS, 'page', errors);
  checkKeys(spec.tooling, TOOLING_KEYS, 'tooling', errors);
  checkKeys(spec.fiducials, FIDUCIALS_KEYS, 'fiducials', errors);
  if (isRecord(spec.copper_fill)) {
    checkKeys(spec.copper_fill, COPPER_FILL_KEYS, 'copper_fill', errors);
  }

  return errors;
}

export function isVec2(v: unknown): v is Vec2 {
  return (
    Array.isArray(v) && v.length === 2 && typeof v[0] === 'number' && typeof v[1] === 'number'
  );
}

/**
 * A problem with the spec, phrased for the user.
 *
 * Mirrors the engine's `SpecError` so preview messages read the same as the
 * ones a render would print.
 */
export class SpecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SpecError';
  }
}
